import { useState } from 'react'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useCreateStep } from '@/features/projects/use-steps'
import { useMoveStep } from '@/features/projects/use-step-moves'
import { StepRow } from './step-row'
import type { ProjectCatalog, StepNode } from '@/api/types'

interface OutlineEditorProps {
  projectId: string
  steps: StepNode[]
  readOnly: boolean
  catalog: ProjectCatalog
}

/**
 * Walk every level of the tree to find which sibling list a given step id lives in. Returns the
 * matching sibling array + index, or null if not found. Used by the drag-end handler so the same
 * `DndContext` can resolve drops at any nesting level — each `StepRow` registers its children's
 * ids in its own `SortableContext`, but the move endpoint takes a global step id.
 */
const findSiblingsContaining = (
  steps: StepNode[],
  stepId: string
): { siblings: StepNode[]; index: number } | null => {
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].id === stepId) return { siblings: steps, index: i }
    const inChild = findSiblingsContaining(steps[i].children, stepId)
    if (inChild) return inChild
  }
  return null
}

export const OutlineEditor = ({ projectId, steps, readOnly, catalog }: OutlineEditorProps) => {
  const create = useCreateStep(projectId)
  const move = useMoveStep(projectId)
  const [adding, setAdding] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')

  // Phase 11a: pointer for desktop, ~250ms long-press for touch (so taps and scrolls survive),
  // keyboard for accessibility (Space to grab, arrows to move, Space to drop).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const submit = () => {
    const next = titleDraft.trim()
    if (!next) {
      setAdding(false)
      setTitleDraft('')
      return
    }
    create.mutate(
      { title: next },
      {
        onSuccess: () => {
          setAdding(false)
          setTitleDraft('')
        },
        onError: () => toast.error('Failed to add step'),
      }
    )
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const fromLoc = findSiblingsContaining(steps, String(active.id))
    const toLoc = findSiblingsContaining(steps, String(over.id))
    if (!fromLoc || !toLoc) return
    // Phase 11a: within-parent drag only via dnd-kit. Cross-parent drag is the same level's
    // sibling list — `dnd-kit`'s SortableContext is per-level, so source and target should
    // belong to the same array. If they don't (the user dragged across nesting levels) skip
    // the move and let them use chevron indent/outdent instead.
    if (fromLoc.siblings !== toLoc.siblings) return
    move.mutate(
      { stepId: String(active.id), newSortOrder: toLoc.index },
      { onError: () => toast.error('Failed to move step') }
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Steps</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {steps.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {readOnly
              ? 'This project has no steps yet.'
              : 'Outline your build as a tree of steps. Each step can hold materials and tools.'}
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <ul className="space-y-1">
              <SortableContext
                items={steps.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {steps.map((step, index) => (
                  <StepRow
                    key={step.id}
                    coords={{
                      step,
                      parent: null,
                      siblings: steps,
                      index,
                      grandparentId: null,
                      parentSiblings: null,
                      parentIndex: null,
                    }}
                    projectId={projectId}
                    allSteps={steps}
                    readOnly={readOnly}
                    catalog={catalog}
                  />
                ))}
              </SortableContext>
            </ul>
          </DndContext>
        )}

        {!readOnly && (
          <div>
            {adding ? (
              <div className="flex gap-2">
                <Input
                  autoFocus
                  placeholder="New step title"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      submit()
                    } else if (e.key === 'Escape') {
                      setAdding(false)
                      setTitleDraft('')
                    }
                  }}
                />
                <Button size="sm" onClick={submit} disabled={create.isPending}>
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setAdding(false)
                    setTitleDraft('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button onClick={() => setAdding(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                {steps.length === 0 ? 'Add first step' : 'Add step'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
